import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookmarkPlus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { PhaseExampleSummary } from "@/lib/types";

/**
 * Save an email the prospect received as one of its phase's example patterns
 * (the `email_examples` the agent draws on when it writes in that phase).
 *
 * <p>Two steps on purpose: the button opens a panel that shows what the phase
 * already carries and what every example costs — each one is sent with every
 * email the agent writes in the phase — and only the confirm writes. Distinct
 * from Feedback, which trains by retrieval; an example is always in the prompt.
 */
export default function SaveAsExampleControl({ emailId }: { emailId: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const summary = useQuery({
    queryKey: ["email-example", emailId],
    queryFn: () => api.get<PhaseExampleSummary>(`/api/v1/emails/${emailId}/example`),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: () => api.post<PhaseExampleSummary>(`/api/v1/emails/${emailId}/example`),
    onSuccess: (s) => {
      queryClient.setQueryData(["email-example", emailId], s);
      queryClient.invalidateQueries({ queryKey: ["strategy-config"] });
      queryClient.invalidateQueries({ queryKey: ["strategy-yaml"] });
      queryClient.invalidateQueries({ queryKey: ["phase-yaml"] });
    },
  });

  const s = summary.data;
  const saved = save.isSuccess || s?.alreadySaved === true;

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Keep this wording as a pattern the agent follows in this phase"
        className="flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-zinc-50 whitespace-nowrap"
      >
        <BookmarkPlus className="h-3.5 w-3.5" aria-hidden />
        Save as example pattern
      </button>

      {open ? (
        <div className="mt-2 space-y-2 rounded-md border border-[var(--border)] bg-zinc-50 p-2">
          {summary.isLoading ? (
            <p className="text-[var(--fg-muted)]">Checking the phase…</p>
          ) : summary.error || !s ? (
            <p className="text-[var(--danger)]">
              {summary.error instanceof ApiError && summary.error.status === 409
                ? "Only an email the prospect received can become an example."
                : "Could not look up this email's phase."}
            </p>
          ) : (
            <>
              <p>
                Becomes example <strong>{saved ? s.exampleCount : s.exampleCount + 1}</strong> for
                phase <code>{s.phaseSlug}</code>
                {s.exampleCount > 0 && !saved
                  ? ` (the phase already has ${s.exampleCount}, about ${s.approxTokens} tokens).`
                  : "."}
              </p>
              <p className="text-[var(--fg-muted)]">
                Every example is sent to the model with every email the agent writes in this
                phase, so each one adds to the prompt size and cost of those emails. The
                greeting, sign-off and unsubscribe footer are left out; anything specific to
                this prospect in the body stays, and can be edited on the Strategies page.
              </p>
              {saved ? (
                <p role="status" className="text-emerald-700">
                  Saved as a pattern for <code>{s.phaseSlug}</code>.
                </p>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => save.mutate()}
                    disabled={save.isPending}
                    className="rounded-md bg-[var(--primary)] px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
                  >
                    {save.isPending ? "Saving…" : "Confirm: save as pattern"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-md border border-[var(--border)] px-3 py-1 text-xs hover:bg-zinc-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
              {save.error ? (
                <p role="alert" className="text-[var(--danger)]">
                  {save.error instanceof ApiError && save.error.status === 422
                    ? "The strategy would not validate with this example added."
                    : "Could not save the example. Please retry."}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
